import { useRef } from 'react'

import { Box, Tooltip } from '@mantine/core'

import { useMutation } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { useScheduledOutreachGuard } from 'src/components/VoE/ScheduledOutreachGuard/useScheduledOutreachGuard'
import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'
import { SEND_VOE_CALL } from 'src/graphql/voiceAgents.graphql'
import IconVoiceMail from 'src/icons/IconVoiceMail'

import { useAgentCallChoice } from './useAgentCallChoice'

/**
 * The company half of the launch scope. Callers use this to decide whether to
 * make room for the button at all; the button still applies the per-VoE half
 * (a phone to dial, and no email to fall back on).
 */
export const companyCanUseVoEVoiceAgent = (
  company?: {
    voeOutboundEnabled?: boolean | null
    voeVoiceAgentEnabled?: boolean | null
  } | null
) =>
  Boolean(company?.voeOutboundEnabled) && Boolean(company?.voeVoiceAgentEnabled)

const VoEVoiceAgentButton = ({
  voeId,
  phoneNumber,
  email,
  canceledOutOfWindow,
  callingHours,
  className,
  targetPhoneNumber,
  tooltip = 'Obtain carrier info via the voice agent',
  guardContact,
}: {
  voeId: string
  phoneNumber?: string | null
  email?: string | null
  /**
   * The VoE was auto-canceled for employment outside the company's
   * verification window — outreach is off, so the agent isn't offered.
   */
  canceledOutOfWindow?: boolean | null
  /**
   * The employer's calling window, in their local time. While it is closed the
   * button still places the call, and its tooltip warns that it is outside
   * hours. Only automated calls are held to the window.
   */
  callingHours?: {
    isOpen: boolean
    closedMessage?: string | null
  } | null
  className?: string
  /**
   * Dial this number instead of the VoE's phone — a number an earlier agent
   * call captured as a referral. The API only accepts a stored referral.
   */
  targetPhoneNumber?: string | null
  tooltip?: string
  guardContact?: (startCall: () => void) => void
}) => {
  const currentCompany = useCurrentCompanyContext()
  const pendingRef = useRef(false)
  const { guardScheduledOutreach, scheduledOutreachPrompt } =
    useScheduledOutreachGuard()
  const { chooseAgentCall, agentCallPrompt } = useAgentCallChoice()

  const [sendVoECall, { loading: calling }] = useMutation(SEND_VOE_CALL, {
    refetchQueries: 'active',
    onCompleted: () => {
      toast('VoE agent call initiated', 'success')
    },
    onError: (error) => {
      toast(error.message, 'error')
    },
  })

  // Same launch scope as sendVoECall: a company with an active VoE agent,
  // only when the employer has a phone and no email (email goes to outreach).
  const canCall =
    companyCanUseVoEVoiceAgent(currentCompany) &&
    Boolean(targetPhoneNumber ?? phoneNumber) &&
    !email?.trim() &&
    !canceledOutOfWindow

  if (!canCall) return null

  const hoursClosed = callingHours ? !callingHours.isOpen : false
  const label = hoursClosed
    ? `${
        callingHours?.closedMessage ??
        'Outside calling hours for this employer.'
      } You can still call now.`
    : tooltip

  return (
    <>
      {agentCallPrompt}
      {scheduledOutreachPrompt}
      <Tooltip label={label} className="text-xs" multiline maw={300}>
        <Box
          component="button"
          type="button"
          disabled={calling}
          aria-label={label}
          data-testid="voe-voice-agent-button"
          className={`border-0 bg-transparent p-0 hover:cursor-pointer hover:bg-doubleNickel-gray-100 hover:stroke-doubleNickel-brand-500 disabled:cursor-not-allowed ${
            className ?? ''
          }`}
          onClick={(event) => {
            event.stopPropagation()
            const placeCall = () => {
              if (pendingRef.current || calling) return
              pendingRef.current = true
              sendVoECall({
                variables: {
                  voeId,
                  ...(targetPhoneNumber && { phoneNumber: targetPhoneNumber }),
                },
              }).finally(() => {
                pendingRef.current = false
              })
            }
            // Offer to schedule the automated calls first. When automation
            // already has the next call scheduled, offer to place that one
            // now so the verification stays automated.
            const startCall = () =>
              chooseAgentCall(voeId, {
                callNow: () =>
                  guardScheduledOutreach(
                    { voeId, kind: 'CALL' },
                    { manual: placeCall }
                  ),
              })
            if (guardContact) {
              guardContact(startCall)
            } else {
              startCall()
            }
          }}
        >
          <IconVoiceMail
            className={`h-3 fill-none stroke-doubleNickel-brand-500 ${
              calling ? 'animate-spin' : ''
            }`}
          />
        </Box>
      </Tooltip>
    </>
  )
}

export default VoEVoiceAgentButton
